/**
 * LOD0 Brand Image presentation math (pure, deterministic, renderer-agnostic).
 *
 * FROZEN law (WO §7–§12): the image is aspect-preserved, centred on the CURRENT
 * operational Territory, inset by a single named safe margin, and (in the
 * renderer) clipped to the EXACT Territory union — never a bounding box. Nothing
 * here persists position/scale/rotation; the renderer supplies the CURRENT
 * territory bounds so expansion recentres automatically. Zoom fade uses named
 * thresholds. This module owns ALL the numbers so no magic literals leak into
 * render code.
 */

/** Fractional inset applied to the territory presentation bounds on every side. */
export const LOD0_BRAND_IMAGE_SAFE_MARGIN = 0.08;

/**
 * FROZEN visibility law (Claudio): the LOD0 brand image is fully visible while
 * the camera is zoomed OUT up to this scale, and disappears entirely once the
 * user zooms IN past it (hard cutoff — no gradual fade band).
 */
export const LOD0_VISIBILITY_MAX_ZOOM = 4.27;

/** At/under this camera zoom the image is fully opaque. */
export const LOD0_FULL_VISIBILITY_MAX_ZOOM = LOD0_VISIBILITY_MAX_ZOOM;

/** Over this camera zoom the image is not rendered at all. */
export const LOD0_FADE_END_ZOOM = LOD0_VISIBILITY_MAX_ZOOM;

export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Deterministic LOD0 opacity for a camera zoom (hard cutoff at 4.27):
 *   zoom <= 4.27  → 1 (fully visible)
 *   zoom >  4.27  → 0 (absent)
 */
export function lod0FadeOpacity(zoom: number): number {
  if (!Number.isFinite(zoom)) return 0;
  return zoom <= LOD0_VISIBILITY_MAX_ZOOM ? 1 : 0;
}

/** True iff the image should be rendered at all at this zoom. */
export function lod0IsVisible(zoom: number): boolean {
  return lod0FadeOpacity(zoom) > 0;
}

/**
 * Aspect-preserving fit of an image into the CURRENT territory bounds, inset by
 * the safe margin, centred. Returns the image rectangle in the SAME coordinate
 * space as `territoryBounds`. The renderer must additionally clip the drawn
 * pixels to the exact territory union path (this rect is the fit envelope, not
 * the clip mask).
 */
export function fitBrandImage(
  territoryBounds: Bounds,
  imageWidth: number,
  imageHeight: number,
  margin: number = LOD0_BRAND_IMAGE_SAFE_MARGIN,
): Bounds {
  const m = Math.max(0, Math.min(0.49, margin));
  const innerW = territoryBounds.w * (1 - 2 * m);
  const innerH = territoryBounds.h * (1 - 2 * m);
  if (innerW <= 0 || innerH <= 0 || imageWidth <= 0 || imageHeight <= 0) {
    return { x: territoryBounds.x + territoryBounds.w / 2, y: territoryBounds.y + territoryBounds.h / 2, w: 0, h: 0 };
  }
  const scale = Math.min(innerW / imageWidth, innerH / imageHeight);
  const w = imageWidth * scale;
  const h = imageHeight * scale;
  const x = territoryBounds.x + (territoryBounds.w - w) / 2;
  const y = territoryBounds.y + (territoryBounds.h - h) / 2;
  return { x, y, w, h };
}
